import React, { useEffect, useState, useCallback } from 'react';
import { usePage } from '../../hooks/usePage';
import { agentsApi, Agent } from '../../api/agents';
import { Card } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Spinner } from '../../components/ui/Spinner';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import { useAuth } from '../../hooks/useAuth';
import { Bot } from 'lucide-react';

const formatContextType = (ct: string) =>
  ct
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');

const Toggle: React.FC<{
  enabled: boolean;
  onChange: () => void;
  disabled?: boolean;
}> = ({ enabled, onChange, disabled }) => (
  <button
    onClick={onChange}
    disabled={disabled}
    aria-label={enabled ? 'Disable agent' : 'Enable agent'}
    style={{
      width: 44,
      height: 24,
      borderRadius: 'var(--radius-full)',
      background: enabled ? 'var(--blue-600)' : 'var(--neutral-400)',
      position: 'relative',
      transition: 'background 200ms ease',
      cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.6 : 1,
      flexShrink: 0,
    }}
  >
    <span
      style={{
        position: 'absolute',
        top: 2,
        left: enabled ? 22 : 2,
        width: 20,
        height: 20,
        borderRadius: '50%',
        background: 'white',
        transition: 'left 200ms ease',
        boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
      }}
    />
  </button>
);

export const Agents: React.FC = () => {
  usePage('AI Agents');
  const { admin } = useAuth();
  const [agents, setAgents] = useState<Agent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pendingToggle, setPendingToggle] = useState<Agent | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  const canManage = admin && admin.role === 'super_admin';

  const fetchAgents = useCallback(async () => {
    try {
      setLoading(true);
      const data = await agentsApi.getAgents();
      setAgents(data);
    } catch {
      setError('Failed to load agents.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void fetchAgents();
  }, [fetchAgents]);

  const executeToggle = async () => {
    if (!pendingToggle) return;
    try {
      setActionLoading(true);
      const updated = await agentsApi.toggleAgent(
        pendingToggle.key,
        !pendingToggle.isEnabled,
      );
      setAgents((prev) =>
        prev.map((a) => (a.key === updated.key ? updated : a)),
      );
      setPendingToggle(null);
    } catch {
      setError('Failed to update agent.');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 64 }}>
        <Spinner size={24} />
      </div>
    );
  }

  return (
    <>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        {error && (
          <div
            style={{
              padding: 12,
              background: 'var(--danger-100)',
              color: 'var(--danger-900)',
              fontSize: 13,
              borderRadius: 'var(--radius-md)',
            }}
          >
            {error}
          </div>
        )}

        {agents.map((agent) => (
          <Card key={agent.key} padding={20}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                gap: 16,
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    marginBottom: 6,
                    flexWrap: 'wrap',
                  }}
                >
                  <Bot size={18} style={{ color: 'var(--blue-600)', flexShrink: 0 }} />
                  <h3
                    style={{
                      fontSize: 15,
                      fontWeight: 600,
                      color: 'var(--neutral-800)',
                      margin: 0,
                    }}
                  >
                    {agent.name}
                  </h3>
                  {agent.isPremium && (
                    <Badge variant="premium">Premium</Badge>
                  )}
                  <Badge variant={agent.isEnabled ? 'success' : 'neutral'}>
                    {agent.isEnabled ? 'Enabled' : 'Disabled'}
                  </Badge>
                </div>

                <p
                  style={{
                    fontSize: 13,
                    color: 'var(--neutral-600)',
                    marginBottom: 12,
                    lineHeight: 1.5,
                  }}
                >
                  {agent.description}
                </p>

                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {agent.contextTypes.map((ct) => (
                    <span
                      key={ct}
                      style={{
                        padding: '3px 8px',
                        fontSize: 11,
                        fontWeight: 500,
                        borderRadius: 'var(--radius-sm)',
                        background: 'var(--blue-100)',
                        color: 'var(--blue-800)',
                      }}
                    >
                      {formatContextType(ct)}
                    </span>
                  ))}
                </div>
              </div>

              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 6,
                  flexShrink: 0,
                }}
              >
                <Toggle
                  enabled={agent.isEnabled}
                  onChange={() => canManage && agent.key !== 'default' && setPendingToggle(agent)}
                  disabled={!canManage || agent.key === 'default'}
                />
                <span
                  style={{
                    fontSize: 11,
                    color: 'var(--neutral-500)',
                    fontWeight: 500,
                  }}
                >
                  {agent.key}
                </span>
                {agent.key === 'default' && (
                <span
                  style={{
                    fontSize: 10,
                    color: 'var(--neutral-400)',
                    fontStyle: 'italic',
                    textAlign: 'center',
                    maxWidth: 80,
                }}
                >
                always on
                </span>
                )}

              </div>
            </div>
          </Card>
        ))}

        {agents.length === 0 && !error && (
          <Card>
            <p
              style={{
                textAlign: 'center',
                color: 'var(--neutral-500)',
                padding: 32,
              }}
            >
              No agents found.
            </p>
          </Card>
        )}
      </div>

      <ConfirmDialog
        isOpen={pendingToggle !== null}
        title={
          pendingToggle?.isEnabled
            ? `Disable ${pendingToggle?.name}?`
            : `Enable ${pendingToggle?.name}?`
        }
        message={
          pendingToggle?.isEnabled
            ? `Users will no longer be able to access ${pendingToggle?.name}. Active conversations will not be affected immediately.`
            : `${pendingToggle?.name} will become available to ${pendingToggle?.isPremium ? 'Premium' : 'all'} users.`
        }
        confirmLabel={pendingToggle?.isEnabled ? 'Disable Agent' : 'Enable Agent'}
        variant={pendingToggle?.isEnabled ? 'danger' : 'primary'}
        loading={actionLoading}
        onConfirm={executeToggle}
        onCancel={() => setPendingToggle(null)}
      />
    </>
  );
};